// TIP-611 — the reference contact, resolved from an email address.
// `Your reference at this company` used to match against DIRECTORY, a fixture
// holding one hardcoded address (ahmed.khan@gulfsteel.ae). Every other email
// missed, so in practice the panel only ever guessed the name from the local
// part. It now asks the backend:
// The response reuses the ORDER PAYLOAD's own field names — requester_full_name
// and friends, the same keys buildOrderPayload sends for section 1 — even
// though what comes back is a reference contact rather than the requester. The
// names are the wire's, so they are honoured here and translated once.
// No React and no store, so the shape can be pinned by a test.
import { COUNTRY_LIST, DEFAULT_DIAL } from "./orderFormFixtures";
import { formatPhone } from "./CbDialPhone";

const text = (v) => (typeof v === "string" ? v.trim() : "");

/**
 * The dial codes longest-first, so +971 is tried before +9 could ever match.
 * Several countries share a code; only the code itself is needed here.
 */
const DIALS = [...new Set(COUNTRY_LIST.map((c) => c.dial))].sort(
  (a, b) => b.length - a.length
);

/* The mobile arrives as one string with no dial code beside it, so a number that carries its country code has to be split off the front — otherwise... */
export const splitMobile = (raw, fallbackDial = DEFAULT_DIAL) => {
  const value = text(raw).replace(/[\s()-]/g, "");
  if (!value) return { dial: fallbackDial, phone: "", applies: false };

  if (value.startsWith("+")) {
    const dial = DIALS.find((d) => value.startsWith(d));
    if (dial) {
      const rest = value.slice(dial.length);
      return { dial, phone: formatPhone(rest, dial), applies: true };
    }
  }

  // Not prefixed, or prefixed with a code the picker does not carry: keep the
  // digits and leave the code alone.
  return {
    dial: fallbackDial,
    phone: formatPhone(value.replace(/^\+/, ""), fallbackDial),
    applies: false,
  };
};

/* The response body → the four fields the reference panel fills. */
export const mapRequesterLookup = (data, { fallbackDial = DEFAULT_DIAL } = {}) => {
  const name = text(data?.requester_full_name);
  const role = text(data?.requester_job_title);
  if (!name && !role) return null;

  const mobile = splitMobile(data?.requester_mobile_number, fallbackDial);

  return {
    name,
    role,
    dial: mobile.dial,
    phone: mobile.phone,
    // Only for the green panel's sentence. The panel has no company field, and
    // the brief's own company is the one selected in the dashboard switcher.
    company: text(data?.requester_company_or_org),
    // The address the backend matched, which should be the one that was typed.
    email: text(data?.requester_business_email),
    // Whether the dial code is the backend's answer or just what the form had.
    dialFromLookup: mobile.applies,
  };
};

/* Which of the panel's fields a match is allowed to LOCK. */
export const lockedFieldsFor = (contact) => {
  if (!contact) return [];
  const locked = [];
  if (contact.name) locked.push("refName");
  if (contact.role) locked.push("refRole");
  if (contact.phone) locked.push("refPhone");
  if (contact.phone && contact.dialFromLookup) locked.push("refDial");
  return locked;
};

export default mapRequesterLookup;
